import { useMemo, useCallback } from 'react';
import { Flex, Grid, Card, CardHeader, CardBody, Text } from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { RiDatabase2Line, RiNodeTree, RiCodeLine } from '@remixicon/react';
import { appTranslationRef } from '../../../../core/i18n';
import { EmptyState } from '../../../../shared/components';
import { EnvSelector } from './env-selector.component';
import { SqlEditor } from './sql-editor.component';
import { QueryResults } from './query-results.component';
import { SchemaGraph } from './schema-graph.component';
import { useDbQuery } from './use-db-query.hook';
import { useEnvironments } from './use-environments.hook';
import { useSchema } from './use-schema.hook';
import styles from './db-explorer.module.css';

export const DbExplorerTab = () => {
  const { t } = useTranslationRef(appTranslationRef);
  const { entity } = useEntity();
  const { loading, error, data, execute } = useDbQuery();

  const { component, environments, selectedEnv, setSelectedEnv } = useEnvironments(entity);
  const { schema, tableCount, fkCount } = useSchema(entity, selectedEnv);

  const entityRef = useMemo(() => stringifyEntityRef(entity), [entity]);

  const handleExecute = useCallback((sql: string) => {
    if (!selectedEnv || !environments) return;
    const selectedEnvironment = environments.find(e => e.name === selectedEnv);
    const envUrl = selectedEnvironment?.externalUrl ?? '';
    execute({ entityRef, env: selectedEnv, envUrl, sql });
  }, [selectedEnv, environments, entityRef, execute]);

  const hosting = entity.metadata.annotations?.['kpc.io/db-hosting'];
  if (hosting && hosting !== 'k8s') {
    return (
      <Card>
        <CardHeader>
          <Flex gap="3" align="center">
            <RiDatabase2Line size={18} className={styles.headerIcon} />
            <Text variant="title-small" weight="bold">
              {t('dbExplorer.unsupportedHosting.title')}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <EmptyState
            title={t('dbExplorer.unsupportedHosting.title')}
            description={t('dbExplorer.unsupportedHosting.description', { hosting })}
          />
        </CardBody>
      </Card>
    );
  }

  if (!component || !environments || environments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Flex gap="3" align="center">
            <RiDatabase2Line size={18} className={styles.headerIcon} />
            <Text variant="title-small" weight="bold">
              {t('dbExplorer.emptyState.title')}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <EmptyState
            title={t('dbExplorer.emptyState.title')}
            description={t('dbExplorer.emptyState.description')}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <Flex direction="column" gap="4">
      {selectedEnv && (
        <div className={styles.envSelectorWrap}>
          <EnvSelector
            environments={environments}
            selected={selectedEnv}
            onSelect={setSelectedEnv}
          />
        </div>
      )}

      <Grid.Root columns="12" gap="3" className={styles.gridStretch}>
        {/* Schema card */}
        <Grid.Item colSpan={{ xs: '12', md: '8' }}>
          <Card>
            <CardHeader>
              <Flex justify="between" align="center">
                <Flex gap="3" align="center">
                  <RiNodeTree size={18} className={styles.headerIcon} />
                  <Text variant="title-small" weight="bold">
                    {t('dbExplorer.schema.title')}
                  </Text>
                </Flex>
                {schema && tableCount > 0 && (
                  <Text variant="body-small" color="secondary">
                    {tableCount} {tableCount === 1 ? t('dbExplorer.schema.table') : t('dbExplorer.schema.tables')}
                    {fkCount > 0 && `, ${fkCount} ${fkCount === 1 ? t('dbExplorer.schema.relation') : t('dbExplorer.schema.relations')}`}
                  </Text>
                )}
              </Flex>
            </CardHeader>
            <CardBody>
              {schema ? (
                <SchemaGraph schema={schema} />
              ) : (
                <Text variant="body-medium" color="secondary">
                  {t('dbExplorer.schema.pending')}
                </Text>
              )}
            </CardBody>
          </Card>
        </Grid.Item>

        {/* SQL Editor card */}
        <Grid.Item colSpan={{ xs: '12', md: '4' }} className={styles.sqlGridItem}>
          <Card className={styles.sqlCard}>
            <CardHeader>
              <Flex gap="3" align="center">
                <RiCodeLine size={18} className={styles.headerIcon} />
                <Text variant="title-small" weight="bold">
                  {t('dbExplorer.sqlEditor.title')}
                </Text>
              </Flex>
            </CardHeader>
            <CardBody className={styles.sqlCardBody}>
              <SqlEditor
                onExecute={handleExecute}
                loading={loading}
                disabled={!selectedEnv}
                stretch
                schema={schema}
              />
            </CardBody>
          </Card>
        </Grid.Item>
      </Grid.Root>

      {(data || error || loading) && (
        <Card>
          <CardBody>
            <QueryResults data={data} loading={loading} error={error} />
          </CardBody>
        </Card>
      )}
    </Flex>
  );
};
